import Phaser from 'phaser';
import { GAME } from '../constants';
import { RunData, getReachableNodes, isRunComplete, startAct2, normalizeRunData } from '../run/RunState';
import { MapNode, NodeType } from '../types';
import { getCurseById } from '../cards/CurseRegistry';
import { PIXEL_THEME, FONT_FAMILY, createPixelPanel, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut } from '../ui/SceneTransitions';
import { createAmbientMotes } from '../ui/ParticleEffects';
import { ACT2_INTRO, ENDING } from '../story/Lore';

const NODE_COLORS: Record<NodeType, number> = {
  combat: 0x48c16f,
  elite: 0xffb037,
  rest: 0x53b9ff,
  event: 0xff7a67,
  boss: 0xff3b30,
};

const NODE_LABELS: Record<NodeType, string> = {
  combat: 'FIGHT',
  elite: 'ELITE',
  rest: 'REST',
  event: 'EVENT',
  boss: 'BOSS',
};

const NODE_ICONS: Record<NodeType, string> = {
  combat: 'ATK',
  elite: 'ELT',
  rest: 'RST',
  event: '?',
  boss: 'BOS',
};

const NODE_DESCRIPTIONS: Record<NodeType, string> = {
  combat: 'Battle a normal enemy. Win to gain a new card.',
  elite: 'Harder enemy, better rewards. Bring your A-game.',
  rest: 'Campfire: Heal, remove a card, or upgrade a modifier.',
  event: 'Random encounter with choices and consequences.',
  boss: 'Act boss. Defeat to complete the run!',
};

export class MapScene extends Phaser.Scene {
  private nodeTooltip!: Phaser.GameObjects.Container;
  private nodeTooltipBg!: Phaser.GameObjects.Rectangle;
  private nodeTooltipText!: Phaser.GameObjects.Text;
  private deckOverlay: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'MapScene' });
  }

  create(data: { run: RunData }): void {
    fadeIn(this);
    const run = normalizeRunData(data.run);
    const cx = GAME.WIDTH / 2;
    this.deckOverlay = [];

    drawPixelBackdrop(this);
    createAmbientMotes(this, 6);

    // Check if act boss is defeated
    if (isRunComplete(run)) {
      if (run.act < 2) {
        // Transition to Act 2 through a story beat
        startAct2(run);
        this.scene.start('StoryScene', { beat: ACT2_INTRO, next: 'MapScene', nextData: { run } });
        return;
      }
      // Run complete after Act 2: ending beat, then the record screen
      this.scene.start('StoryScene', {
        beat: ENDING,
        next: 'GameOverScene',
        nextData: {
          victory: true,
          turnsPlayed: run.battlesWon,
          playerHp: run.playerHp,
          maxHp: run.playerMaxHp,
          isRunComplete: true,
          combos: run.combosTriggered,
          elites: run.elitesDefeated,
          bosses: run.bossesDefeated,
          relics: run.relics.length,
          run,
        },
      });
      return;
    }

    const titlePanel = createPixelPanel(this, cx, 28, GAME.WIDTH - 80, 46, {
      fill: 0x20172a,
      stroke: 0xbf6138,
      accent: 0xffd166,
    });
    titlePanel.elements.forEach(el => el.setDepth(1));

    this.add.text(cx, 24, `ACT ${run.act} - Choose Your Path`, {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#ffe5aa',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    this.createLoadoutStrip(run);

    // Create node tooltip
    this.createNodeTooltip();

    const reachable = getReachableNodes(run);
    const reachableIds = new Set(reachable.map(n => n.id));

    // Layout constants
    const mapTop = 124;
    const mapBottom = GAME.HEIGHT - 80;
    const rowCount = run.map.length;
    const rowSpacing = (mapBottom - mapTop) / (rowCount - 1);

    // Draw connections as dotted paths
    const pathG = this.add.graphics();
    for (const row of run.map) {
      for (const node of row) {
        const nx = this.getNodeX(node, row.length);
        const ny = mapTop + node.row * rowSpacing;
        for (const connId of node.connections) {
          const nextRow = run.map[node.row + 1];
          if (!nextRow) continue;
          const target = nextRow.find(n => n.id === connId);
          if (!target) continue;
          const tx = this.getNodeX(target, nextRow.length);
          const ty = mapTop + target.row * rowSpacing;

          const isPath = run.visitedNodeIds.includes(node.id) &&
            (run.visitedNodeIds.includes(target.id) || reachableIds.has(target.id));
          const lineColor = isPath ? 0x8888aa : 0x333344;
          const alpha = isPath ? 0.7 : 0.35;
          const dx = tx - nx;
          const dy = ty - ny;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const spacing = isPath ? 8 : 12;
          const count = Math.floor(dist / spacing);
          pathG.fillStyle(lineColor, alpha);
          for (let d = 0; d <= count; d++) {
            const t = d / count;
            pathG.fillCircle(nx + dx * t, ny + dy * t, isPath ? 2 : 1.5);
          }
        }
      }
    }

    // Draw nodes
    for (const row of run.map) {
      for (const node of row) {
        const nx = this.getNodeX(node, row.length);
        const ny = mapTop + node.row * rowSpacing;
        this.drawNode(node, nx, ny, run, reachableIds);
      }
    }

    // Legend
    const legendPanel = createPixelPanel(this, cx, GAME.HEIGHT - 55, GAME.WIDTH - 120, 42, {
      fill: 0x141d30,
      stroke: 0x4a6387,
      accent: 0x88a9d9,
    });
    legendPanel.elements.forEach(el => el.setDepth(1));
    const legendY = GAME.HEIGHT - 55;
    const legendItems: { type: NodeType; x: number }[] = [
      { type: 'combat', x: 140 },
      { type: 'elite', x: 310 },
      { type: 'rest', x: 470 },
      { type: 'event', x: 630 },
      { type: 'boss', x: 810 },
    ];
    for (const item of legendItems) {
      this.add.rectangle(item.x - 12, legendY, 10, 10, NODE_COLORS[item.type], 0.8)
        .setStrokeStyle(1, 0x10131a, 0.8)
        .setDepth(2);
      this.add.text(item.x, legendY, NODE_LABELS[item.type], {
        fontSize: '11px', fontFamily: FONT_FAMILY, color: '#bcc7e7',
      }).setOrigin(0, 0.5).setDepth(2);
    }

    // Deck view button
    const deckBtn = this.add.text(cx, GAME.HEIGHT - 25, '[ VIEW LOADOUT  D ]', {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#ffe2a0',
      backgroundColor: '#322638',
      padding: { x: 12, y: 4 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(3);

    deckBtn.on('pointerover', () => deckBtn.setColor('#fff3cb'));
    deckBtn.on('pointerout', () => deckBtn.setColor('#ffe2a0'));
    deckBtn.on('pointerdown', () => this.toggleDeckView(run));

    this.input.keyboard?.on('keydown-D', () => this.toggleDeckView(run));
    this.input.keyboard?.on('keydown-ESC', () => this.closeDeckView());
  }

  private createNodeTooltip(): void {
    this.nodeTooltipBg = this.add.rectangle(0, 0, 300, 40, 0x111122, 0.95)
      .setStrokeStyle(1, 0x555577);
    this.nodeTooltipText = this.add.text(0, 0, '', {
      fontSize: '11px', fontFamily: FONT_FAMILY, color: '#ccccdd',
      align: 'center', wordWrap: { width: 280 },
    }).setOrigin(0.5);
    this.nodeTooltip = this.add.container(0, 0, [this.nodeTooltipBg, this.nodeTooltipText]);
    this.nodeTooltip.setDepth(100);
    this.nodeTooltip.setVisible(false);
  }

  private createLoadoutStrip(run: RunData): void {
    const panelY = 84;
    const panelWidth = GAME.WIDTH - 80;
    const panelHeight = 44;
    const panel = createPixelPanel(this, GAME.WIDTH / 2, panelY, panelWidth, panelHeight, {
      fill: 0x141d30,
      stroke: 0x4f7e9a,
      accent: 0xa3d9ee,
    });
    panel.elements.forEach(el => el.setDepth(1));

    this.add.text(56, panelY - 12, 'RUN LOADOUT', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#aee9ff',
      fontStyle: 'bold',
    }).setDepth(2);

    const summary = `HP ${run.playerHp}/${run.playerMaxHp} | W ${run.battlesWon} | Cmb ${run.combosTriggered} | Deck ${run.deckNumbers.length} | Rel ${run.relics.length} | Abl ${run.abilities.length} | Cur ${run.curses.length} | D details`;
    this.add.text(28, panelY + 2, summary, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: '#d2dcff',
      wordWrap: { width: panelWidth - 44 },
    }).setDepth(2);

    this.tweens.add({
      targets: panel.bg,
      alpha: 0.92,
      duration: 1300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private showNodeTooltip(type: NodeType, x: number, y: number): void {
    this.nodeTooltipText.setText(NODE_DESCRIPTIONS[type]);
    const bounds = this.nodeTooltipText.getBounds();
    this.nodeTooltipBg.setSize(bounds.width + 20, bounds.height + 12);
    const ty = Math.max(50, y - 40);
    const tx = Phaser.Math.Clamp(x, 160, GAME.WIDTH - 160);
    this.nodeTooltip.setPosition(tx, ty);
    this.nodeTooltip.setVisible(true);
  }

  private getNodeX(node: MapNode, rowSize: number): number {
    const spacing = 200;
    const totalWidth = (rowSize - 1) * spacing;
    return GAME.WIDTH / 2 - totalWidth / 2 + node.col * spacing;
  }

  private drawNode(
    node: MapNode, x: number, y: number,
    run: RunData, reachableIds: Set<string>,
  ): void {
    const visited = run.visitedNodeIds.includes(node.id);
    const isReachable = reachableIds.has(node.id);
    const isCurrent = run.currentNodeId === node.id;
    const color = NODE_COLORS[node.type];

    const size = node.type === 'boss' ? 62 : 50;
    const alpha = visited ? 0.3 : isReachable ? 1 : 0.4;

    // Glow ring for reachable nodes
    if (isReachable && !visited) {
      const glow = this.add.circle(x, y, size / 2 + 8, color, 0.15);
      this.tweens.add({
        targets: glow,
        scale: 1.2,
        alpha: 0.05,
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Node body with corner ornaments
    const nodeBody = this.add.rectangle(x, y, size, size, color, alpha)
      .setStrokeStyle(2, 0x12141b, 0.8);
    this.add.rectangle(x - size / 2 + 4, y - size / 2 + 4, 3, 3, 0xffffff, 0.45);
    this.add.rectangle(x + size / 2 - 4, y + size / 2 - 4, 3, 3, 0xffffff, 0.45);
    this.add.rectangle(x + size / 2 - 4, y - size / 2 + 4, 3, 3, 0xffffff, 0.25);
    this.add.rectangle(x - size / 2 + 4, y + size / 2 - 4, 3, 3, 0xffffff, 0.25);

    if (isCurrent) {
      nodeBody.setStrokeStyle(3, 0xffd700);
    } else if (isReachable) {
      nodeBody.setStrokeStyle(2, 0xffffff, 0.8);
    } else if (visited) {
      nodeBody.setStrokeStyle(1, 0x444444);
    }

    // Pixel-art icon
    this.drawNodeIcon(x, y, node.type, visited ? 0.3 : 1);

    // Label
    this.add.text(x, y + size / 2 + 9, NODE_LABELS[node.type], {
      fontSize: '7px',
      fontFamily: FONT_FAMILY,
      color: visited ? '#444444' : '#cfd8f5',
    }).setOrigin(0.5);

    // Clickable + tooltip if reachable
    if (isReachable && !visited) {
      this.tweens.add({
        targets: nodeBody,
        scale: 1.05,
        duration: 950,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      nodeBody.setInteractive({ useHandCursor: true });
      nodeBody.on('pointerover', () => {
        nodeBody.setStrokeStyle(3, 0xffd700);
        nodeBody.setScale(1.12);
        this.showNodeTooltip(node.type, x, y);
      });
      nodeBody.on('pointerout', () => {
        nodeBody.setStrokeStyle(2, 0xffffff, 0.8);
        nodeBody.setScale(1);
        this.nodeTooltip.setVisible(false);
      });
      nodeBody.on('pointerdown', () => {
        this.nodeTooltip.setVisible(false);
        this.selectNode(node, run);
      });
    }
  }

  private drawNodeIcon(x: number, y: number, type: NodeType, alpha: number): void {
    // 5x5 pixel-art icons for each node type
    const W = 0xffffff;
    const icons: Record<NodeType, (number | null)[][]> = {
      combat: [ // sword
        [null, null, null, null,    W],
        [null, null, null,    W, null],
        [null,    W,    W, null, null],
        [null,    W, null, null, null],
        [   W, null, null, null, null],
      ],
      elite: [ // skull
        [null,    W,    W,    W, null],
        [   W, null,    W, null,    W],
        [   W,    W,    W,    W,    W],
        [null,    W, null,    W, null],
        [null, null,    W, null, null],
      ],
      rest: [ // campfire
        [null, null,    W, null, null],
        [null,    W,    W,    W, null],
        [null,    W,    W,    W, null],
        [   W,    W,    W,    W,    W],
        [null, null,    W, null, null],
      ],
      event: [ // question mark
        [null,    W,    W,    W, null],
        [null, null, null,    W, null],
        [null, null,    W, null, null],
        [null, null, null, null, null],
        [null, null,    W, null, null],
      ],
      boss: [ // crown
        [   W, null,    W, null,    W],
        [   W,    W,    W,    W,    W],
        [   W,    W,    W,    W,    W],
        [null,    W,    W,    W, null],
        [null,    W,    W,    W, null],
      ],
    };

    const grid = icons[type];
    const pixelSize = 4;
    const gridSize = 5;
    const offset = (gridSize * pixelSize) / 2;
    const g = this.add.graphics();
    g.setAlpha(alpha);

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const c = grid[row][col];
        if (c !== null) {
          g.fillStyle(c, 1);
          g.fillRect(
            x - offset + col * pixelSize,
            y - offset + row * pixelSize,
            pixelSize,
            pixelSize,
          );
        }
      }
    }
  }

  private selectNode(node: MapNode, run: RunData): void {
    run.currentNodeId = node.id;
    run.visitedNodeIds.push(node.id);

    fadeOut(this).then(() => {
      switch (node.type) {
        case 'combat':
        case 'elite':
          this.scene.start('CombatScene', { run, nodeType: node.type });
          break;
        case 'boss':
          this.scene.start('CombatScene', { run, nodeType: 'boss' });
          break;
        case 'rest':
          this.scene.start('RestScene', { run });
          break;
        case 'event':
          this.scene.start('EventScene', { run });
          break;
      }
    });
  }

  private toggleDeckView(run: RunData): void {
    if (this.deckOverlay.length > 0) {
      this.closeDeckView();
      return;
    }
    this.showDeckView(run);
  }

  private closeDeckView(): void {
    if (this.deckOverlay.length === 0) return;
    for (const el of this.deckOverlay) {
      el.destroy();
    }
    this.deckOverlay = [];
  }

  private getDeckEntryLabel(run: RunData, cardNumber: number, count: number): string {
    const mod = run.modifiers.get(cardNumber);
    const countText = count > 1 ? `x${count}` : '';
    if (!mod) return `${cardNumber}${countText}`;
    const triggerLabel = mod.trigger === 'attack' ? 'ATK'
      : mod.trigger === 'defense' ? 'DEF' : 'ABL';
    const effectName = mod.effect.type === 'extra_damage' ? 'dmg'
      : mod.effect.type === 'extra_block' ? 'blk'
      : mod.effect.type === 'heal' ? 'heal'
      : mod.effect.type === 'buff_attack' ? 'atk+'
      : mod.effect.type === 'debuff_enemy' ? 'debuf'
      : mod.effect.type === 'draw_penalty' ? 'draw-'
      : mod.effect.type === 'zone_cap_attack' ? 'capA'
      : mod.effect.type === 'zone_cap_defense' ? 'capD'
      : mod.effect.type === 'zone_cap_ability' ? 'capL'
      : mod.effect.type === 'zone_cap_all' ? 'cap+'
      : 'draw+';
    return `${cardNumber}${countText} [${triggerLabel}:${effectName}+${mod.effect.value}]`;
  }

  private showDeckView(run: RunData): void {
    this.closeDeckView();
    const overlayPanel = createPixelPanel(
      this,
      GAME.WIDTH / 2,
      GAME.HEIGHT / 2,
      GAME.WIDTH - 100,
      GAME.HEIGHT - 100,
      { fill: 0x111728, stroke: 0xc5673b, accent: 0xffd68f, alpha: 0.96 },
    );
    overlayPanel.elements.forEach(el => el.setDepth(50));
    overlayPanel.bg.setInteractive();
    const overlay = overlayPanel.bg;

    const title = this.add.text(GAME.WIDTH / 2, 80, 'YOUR DECK', {
      fontSize: '14px', fontFamily: FONT_FAMILY, color: '#ffe4aa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(51);

    const hint = this.add.text(GAME.WIDTH / 2, 104, 'D or ESC to close', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9cb3df',
    }).setOrigin(0.5).setDepth(51);

    const elements: Phaser.GameObjects.GameObject[] = [overlay, title, hint];
    const groupedCounts = new Map<number, number>();
    for (const num of run.deckNumbers) {
      groupedCounts.set(num, (groupedCounts.get(num) ?? 0) + 1);
    }
    const grouped = Array.from(groupedCounts.entries()).sort((a, b) => a[0] - b[0]);

    const cols = 4;
    const startX = GAME.WIDTH / 2 - (cols - 1) * 116;
    const startY = 156;
    for (let i = 0; i < grouped.length; i++) {
      const [num, count] = grouped[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * 232;
      const y = startY + row * 54;

      const mod = run.modifiers.get(num);
      const label = this.getDeckEntryLabel(run, num, count) + (num < 0 ? ' NEG' : '');
      const rowBg = this.add.rectangle(x, y, 220, 42, 0x2a2233, 0.82)
        .setStrokeStyle(1, mod ? 0xffcc77 : 0x5f678c, 0.8)
        .setDepth(51);
      const rowText = this.add.text(x, y, label, {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: mod ? '#ffdf9f' : num < 0 ? '#ff8f9a' : '#dfe6ff',
        align: 'center',
        wordWrap: { width: 208 },
      }).setOrigin(0.5).setDepth(52);
      elements.push(rowBg, rowText);
    }

    // Negative explanation
    if (run.deckNumbers.some(n => n < 0)) {
      const negText = this.add.text(GAME.WIDTH / 2, startY + Math.ceil(grouped.length / cols) * 54 + 10,
        'NEG cards: ATK heals enemy, DEF weakens block. Modifiers still work!', {
          fontSize: '11px', fontFamily: FONT_FAMILY, color: '#ff6666',
        }).setOrigin(0.5).setDepth(51);
      elements.push(negText);
    }

    if (run.curses.length > 0) {
      const curseNames = run.curses.map(curseId => getCurseById(curseId)?.name ?? curseId);
      const cursesText = this.add.text(
        GAME.WIDTH / 2,
        GAME.HEIGHT - 160,
        `Curses: ${curseNames.join(', ')}`,
        {
          fontSize: '11px',
          fontFamily: FONT_FAMILY,
          color: '#ff8899',
          align: 'center',
          wordWrap: { width: GAME.WIDTH - 180 },
        },
      ).setOrigin(0.5).setDepth(51);
      elements.push(cursesText);
    }

    if (run.abilities.length > 0) {
      const abilitiesText = this.add.text(
        GAME.WIDTH / 2,
        GAME.HEIGHT - 130,
        `Abilities: ${run.abilities.map(a => a.name).join('  |  ')}`,
        {
          fontSize: '11px',
          fontFamily: FONT_FAMILY,
          color: '#ffd166',
          align: 'center',
          wordWrap: { width: GAME.WIDTH - 180 },
        },
      ).setOrigin(0.5).setDepth(51);
      elements.push(abilitiesText);
    }

    const closeBtn = this.add.text(GAME.WIDTH / 2, GAME.HEIGHT - 80, '[ CLOSE ]', {
      fontSize: '11px', fontFamily: FONT_FAMILY, color: '#ffffff',
      backgroundColor: '#3a2830', padding: { x: 16, y: 8 },
    }).setOrigin(0.5).setDepth(51).setInteractive({ useHandCursor: true });
    elements.push(closeBtn);

    closeBtn.on('pointerdown', () => this.closeDeckView());
    this.deckOverlay = elements;
  }
}
